import { cx } from "@emotion/css";
import { EmotionJSX } from "@emotion/react/dist/declarations/src/jsx-namespace";
import { Box, Stack, Typography } from "@mui/material";
import { DRAWER_INFINITE_SCROLL_PAGE_SIZE, SEARCH_DELAY_DEBOUNCE } from "app/appConstants";
import { AppGlobalClassKeys } from "app/appGlobalStyles";
import { GenericAbortSignal } from "axios";
import { MprcContainer, MprcRequestErrorReport, MprcSpinnerIndicator, MprcTextMuted } from "components";
import { IconButtonClearSearch } from "components/iconButtons/IconButtonClearSearch";
import { TextFieldFilter } from "components/MprcAdvFilter";
import { MprcAlertNoRecordDrawer } from "components/MprcAlertNoRecordDrawer";
import MprcAutoSizer from "components/MprcAutoSizer";
import { MprcBoxDrawer } from "components/MprcBoxDrawer";
import { isCanceledError } from "providers/CoreApiProvider";
import React, { ForwardedRef, ReactNode, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FixedSizeList, ListChildComponentProps } from "react-window";
import InfiniteLoader from "react-window-infinite-loader";
import { GenericListFilter, GenericListItemsType, PaginatedList } from "views/common/GenericListDrawer";

////////////////////////////////////////////////////////////////////////////////

export interface IContentHeaderProps {
    searchText: string;
}

interface IProps<TDto extends unknown, TPage extends PaginatedList<TDto>> {
    open?: boolean;
    title?: React.ReactNode;
    subtitle?: React.ReactNode;
    filterRight?: React.ReactNode;
    footer?: React.ReactNode;
    itemHeight?: number;
    selection?: "none" | "normal" | "history";
    emptyFilterMessage?: string;
    emptyDataMessage?: string;
    showCounter?: (data: IContentHeaderProps) => boolean;
    contentHeader?: (data: IContentHeaderProps) => React.ReactNode;
    recordCounterFormater?: (records: number) => string;
    onClose: () => void;
    onGetData: (filter: GenericListFilter, signal: GenericAbortSignal | undefined) => Promise<TPage>;
    onViewerClick?: (item: TDto) => void;
    renderRow: (props: ListChildComponentProps<GenericListItemsType<TDto>>) => EmotionJSX.Element;
}

export type GenericListDrawerHandle = {
    resetSearch: () => void;
    refresh: () => void;
};

function GenericListDrawerComponent<TDto extends unknown, TPage extends PaginatedList<TDto>>(props: IProps<TDto, TPage>, forwardRef: ForwardedRef<GenericListDrawerHandle>): ReactNode {

    const {
        open, title, subtitle, emptyDataMessage, emptyFilterMessage, filterRight,
        footer, itemHeight = 40, selection = "none",
        showCounter, recordCounterFormater, contentHeader, onClose, onGetData, onViewerClick, renderRow
    } = props;

    const { t } = useTranslation();

    const [error, setError] = useState<any>(null);
    const [searchText, setSearchText] = useState<string>("");
    const [firstLoad, setFirstLoad] = useState<boolean>(true);
    const [loading, setLoading] = useState<boolean>(true);
    const [items, setItems] = useState<TDto[]>([]);

    const currentPage = useRef<number>(1);
    const endOfRecords = useRef<boolean>(true);
    const infiniteLoaderRef = useRef<InfiniteLoader>(null);
    const hasMountedRef = useRef(false);
    const rowsLoaded = useRef<number>(0);
    const totalRows = useRef<number>(0);

    const searchTextRef = useRef<HTMLInputElement>(null);
    const currentSearchText = useRef<string>("");
    const abortControllerRef = useRef<AbortController>(undefined);
    const ignoreSelectionHistory = useRef<boolean>(selection != "history");

    useImperativeHandle(forwardRef, () => ({
        resetSearch,
        refresh
    }));

    const refresh = () => {
        currentPage.current = 1;
        loadData();
    };

    async function loadData() {
        setError(null);

        try {

            if (currentPage.current == 1) {
                rowsLoaded.current = 0;
                setFirstLoad(true);
                setItems([]);
            }

            setLoading(true);

            abortControllerRef.current = new AbortController();

            const result = await onGetData({
                searchText: currentSearchText.current,
                pageNumber: currentPage.current,
                pageSize: DRAWER_INFINITE_SCROLL_PAGE_SIZE,
                ignoreSelectionHistory: ignoreSelectionHistory.current
            }, abortControllerRef.current.signal);

            totalRows.current = result.totalRows;
            rowsLoaded.current += result.items.length;

            if (currentPage.current == 1) {
                // Reseta o cache.
                if (hasMountedRef.current) {
                    if (infiniteLoaderRef.current) {
                        infiniteLoaderRef.current.resetloadMoreItemsCache(true);
                    }
                }
                // Inicializa o array de itens.
                setItems(result.items);
            }
            else {
                // Adiciona mais itens no array.
                setItems([...items, ...result.items]);
            }

            // Há mais registros?
            endOfRecords.current = 'totalRows' in result ? result.totalRows == rowsLoaded.current : true;

            setTimeout(() => searchTextRef.current?.focus(), 100);
        }
        catch (e) {
            if (!isCanceledError(e)) {
                setError(e);
                endOfRecords.current = true;
            }
        }
        finally {
            setLoading(false);
            setFirstLoad(false);
            abortControllerRef.current = undefined;
        }
    }

    const localRenderRow = (props: ListChildComponentProps<GenericListItemsType<TDto>>) => {
        const { index, style } = props;

        if (index > items.length - 1 && loading) {
            return (
                <Box style={style} sx={{ mt: 1 }}>
                    <MprcSpinnerIndicator />
                </Box>
            );
        }

        if (index > items.length - 1)
            console.error("Ops, index greather than array lenght.", index, items.length);

        return renderRow(props);
    };

    ///
    const loadNextPage = (startIndex: number, stopIndex: number) => {
        currentPage.current++;
        return new Promise<void>((resolve) => {
            loadData().then(() => resolve());
        });
    };

    ///
    const resetSearch = () => {
        hasMountedRef.current = true;
        currentPage.current = 1;
        currentSearchText.current = searchText;
        loadData();
    };

    // Se há mais itens para ser carregado, adicionamos UMA LINHA para que o Indicador de Carregamento apareça.
    const itemCount = !endOfRecords.current ? items.length + 1 : items.length;

    // Permite apenas uma página de items pode vez.
    const loadMoreItems = loading ? () => { } : loadNextPage;

    // Verifica se a linha está carregada, exceto o Indicador de Carregamento.
    const isItemLoaded = (index: number) => endOfRecords.current || index < items.length;

    ////////////////////////////////////////////////////////////////////////////////
    /// Effects
    ////////////////////////////////////////////////////////////////////////////////

    /// Executa a pesquisa depois de 600ms.
    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            abortControllerRef.current?.abort();
            resetSearch();
        }, SEARCH_DELAY_DEBOUNCE);

        return () => {
            clearTimeout(delayDebounce);
            abortControllerRef.current?.abort();
        };
    }, [searchText]);


    ////////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////////

    return (
        <MprcBoxDrawer
            open={open}
            onClose={onClose}
            title={title}
            disableScroll
            subtitle={
                typeof (subtitle) === "string"
                    ? (
                        <div title={subtitle}>
                            <span>{subtitle}</span>
                        </div>
                    )
                    : subtitle
            }
            header={
                <>
                    <MprcContainer margin="dense">
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                            <TextFieldFilter
                                autoFocus
                                fullWidth
                                margin="dense"
                                autoComplete="off"
                                placeholder={t("Search.TextPlaceholder", { ns: "shared" })}
                                value={searchText}
                                onChange={() => {
                                    setSearchText(searchTextRef.current?.value ?? "");
                                }}
                                inputRef={searchTextRef}
                                slotProps={{
                                    input: {
                                        endAdornment:
                                            searchText != "" &&
                                            <IconButtonClearSearch
                                                size="small"
                                                disableFocusRipple
                                                disableRipple
                                                disableTouchRipple
                                                sx={{ p: 0 }}
                                                onClick={() => setSearchText("")}
                                            />

                                    }
                                }}
                            />

                            {filterRight}
                        </Stack>
                    </MprcContainer>

                    {(showCounter == undefined || showCounter({ searchText: currentSearchText.current })) &&
                        <MprcTextMuted>
                            {
                                totalRows.current > 0 && !firstLoad
                                    ? <Typography variant="caption">{recordCounterFormater ? recordCounterFormater(totalRows.current) : <span>{t("Filter.RecordsFound", { count: totalRows.current, ns: "shared" })}</span>}</Typography>
                                    : (totalRows.current == 0 || items.length == 0) && currentSearchText.current.length == 0 && !firstLoad
                                        ? <MprcAlertNoRecordDrawer message={emptyDataMessage ?? t("shared:Filter.ThereAreNoResults")} />
                                        : (totalRows.current == 0 || items.length == 0) && currentSearchText.current.length > 0 && !firstLoad
                                            ? <MprcAlertNoRecordDrawer message={emptyFilterMessage ?? t("shared:Filter.NoRecordsFound")} />
                                            : null
                            }
                        </MprcTextMuted>
                    }

                    {rowsLoaded.current > 0 && contentHeader && contentHeader({ searchText: currentSearchText.current })}
                </>
            }
            content={
                <>
                    {
                        error
                            ? <MprcRequestErrorReport error={error} />
                            : loading && items.length == 0
                                ? <MprcSpinnerIndicator />
                                : (
                                    <MprcAutoSizer>
                                        {
                                            ({ width, height }) =>

                                                <InfiniteLoader
                                                    isItemLoaded={isItemLoaded}
                                                    itemCount={itemCount}
                                                    loadMoreItems={loadMoreItems}
                                                    ref={infiniteLoaderRef}
                                                    threshold={10}
                                                    minimumBatchSize={10}
                                                >
                                                    {({ onItemsRendered, ref }) => (
                                                        <FixedSizeList
                                                            height={height}
                                                            width={width}
                                                            itemData={{ items, onViewerClick }}
                                                            itemCount={itemCount}
                                                            itemSize={itemHeight}
                                                            onItemsRendered={onItemsRendered}
                                                            ref={ref}
                                                            className={cx(AppGlobalClassKeys.drawerFixedSizeList, AppGlobalClassKeys.thinScrollbar)}
                                                        >
                                                            {localRenderRow}
                                                        </FixedSizeList>
                                                    )}
                                                </InfiniteLoader>
                                        }
                                    </MprcAutoSizer>
                                )
                    }
                </>
            }
            footer={footer}
        />
    );
}

export const GenericListDrawer = React.forwardRef(GenericListDrawerComponent) as
    <TDto>(props: IProps<TDto, PaginatedList<TDto>> & { ref?: React.ForwardedRef<GenericListDrawerHandle>; }) => ReturnType<typeof GenericListDrawerComponent>;
